import { IUser } from '@/models/user.model'
import { webApiService } from '@/services'
import { blobToBase64 } from '@/utils/blobToBase64.util'
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { MdPhotoCamera, MdReceiptLong, MdRefresh, MdUploadFile, MdVisibility } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { TradeinContext } from '../context/TradeinContext'

interface IOwnProps {
  idTradein: number
  /* Ticket ya cargado: si viene, el botón parte en modo visor. Cuando el componente se monta
     dentro del TradeinProvider se puede omitir y se toma de ctx.tradein.ticket_url. */
  ticketUrl?: string
  /* Se avisa al listado para que refresque después de subir el ticket. */
  onDone?: () => void
}

/* Foto del ticket de venta. Se intenta abrir la cámara del equipo (celular, tablet o PC) con
   getUserMedia; si el navegador no la entrega (sin permiso, sin cámara o sin https) se cae al
   input de archivo, que en móvil abre la cámara nativa y en PC el explorador de archivos. */
const UploadTicket: React.FC<IOwnProps> = ({ idTradein, ticketUrl, onDone }) => {
  const userState = useSelector((store: { user: IUser }) => store.user)
  // useContext directo y no useTradeinContext: el listado monta este botón fuera del provider.
  const tradeinCtx = useContext(TradeinContext)
  const savedTicket = String(ticketUrl ?? tradeinCtx?.ctx.tradein.ticket_url ?? '')
  const { slug } = useParams()
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<'view' | 'capture'>('capture')
  const [starting, setStarting] = useState(false)
  const [cameraReady, setCameraReady] = useState(false)
  const [cameraError, setCameraError] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState('')
  const [sending, setSending] = useState(false)
  // Se guarda lo recién subido para no depender de que el listado refresque para poder verlo.
  const [savedUrl, setSavedUrl] = useState(savedTicket)
  const [savedLocal, setSavedLocal] = useState('')

  const hasTicket = savedUrl !== '' || savedLocal !== ''
  const viewSrc = savedUrl !== '' ? savedUrl : savedLocal

  /* Si el listado o el contexto refrescan y traen el ticket, esa url manda sobre la copia local. */
  useEffect(() => {
    if (savedTicket !== '') setSavedUrl(savedTicket)
  }, [savedTicket])

  const stopCamera = () => {
    if (streamRef.current !== null) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    if (videoRef.current !== null) videoRef.current.srcObject = null
    setCameraReady(false)
  }

  const startCamera = async () => {
    setCameraError('')
    setStarting(true)
    try {
      if (navigator.mediaDevices?.getUserMedia === undefined) throw new Error('sin soporte')
      // facingMode ideal: en celular/tablet toma la cámara trasera, en PC la única disponible.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false
      })
      streamRef.current = stream
      if (videoRef.current !== null) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
      setCameraReady(true)
    } catch (error: any) {
      console.log('🚀 ~ startCamera ~ error:', error)
      stopCamera()
      setCameraError('No pudimos abrir la cámara. Revisa los permisos del navegador o adjunta la foto desde el dispositivo.')
    } finally {
      setStarting(false)
    }
  }

  /* La cámara se prende solo al capturar y se apaga siempre al cerrar el modal o al desmontar. */
  useEffect(() => {
    if (open && mode === 'capture' && file === null) startCamera()
    if (!open) stopCamera()
    return () => stopCamera()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode])

  const handleOpen = () => {
    setMode(hasTicket ? 'view' : 'capture')
    setOpen(true)
  }

  const clearPreview = () => {
    if (preview !== '') URL.revokeObjectURL(preview)
    setFile(null)
    setPreview('')
  }

  const setCapture = (captured: File) => {
    if (preview !== '') URL.revokeObjectURL(preview)
    setFile(captured)
    setPreview(URL.createObjectURL(captured))
    stopCamera()
  }

  /* El ticket es texto chico: se comprime menos que el resto de la app para poder leerlo. */
  const compress = async (source: File) => {
    try {
      const imageCompression = (await import('browser-image-compression')).default
      const compressed = await imageCompression(source, { maxSizeMB: 1.5, maxWidthOrHeight: 1600, useWebWorker: true })
      return compressed as File
    } catch (error: any) {
      console.log('🚀 ~ compress ~ error:', error)
      return source
    }
  }

  const takePhoto = async () => {
    const video = videoRef.current
    if (video === null || video.videoWidth === 0) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const context = canvas.getContext('2d')
    if (context === null) return
    context.drawImage(video, 0, 0, canvas.width, canvas.height)
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92))
    if (blob === null) {
      enqueueSnackbar('No fue posible tomar la fotografía.', { variant: 'error' })
      return
    }
    const captured = new File([blob], `ticket_${idTradein}.jpg`, { type: 'image/jpeg' })
    setCapture(await compress(captured))
  }

  const openFilePicker = () => {
    if (fileInputRef.current === null) return
    fileInputRef.current.value = ''
    fileInputRef.current.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0]
    if (selected === undefined) return
    setCapture(await compress(selected))
  }

  const retake = () => {
    clearPreview()
    startCamera()
  }

  const handleClose = () => {
    if (sending) return
    stopCamera()
    clearPreview()
    setCameraError('')
    setOpen(false)
  }

  const handleSend = async () => {
    if (file === null) return
    setSending(true)
    try {
      const base64 = await blobToBase64(file)
      const res = await webApiService.uploadTicket({
        id_tradein: idTradein,
        seller: slug,
        store_user: String(userState.id),
        file: { name: file.name, base64: String(base64) }
      })
      const url = String(res?.data?.url ?? res?.data?.ticket_url ?? res?.url ?? '').trim()
      enqueueSnackbar('Ticket de venta cargado con éxito', { variant: 'success' })
      setSending(false)
      stopCamera()
      if (url !== '') {
        // Con url del backend se descarta la foto local; si no la devuelve, se deja la foto
        // recién tomada para poder verla mientras el listado no traiga el ticket.
        clearPreview()
        setSavedUrl(url)
        setSavedLocal('')
        if (tradeinCtx !== null) {
          const { ctx, saveCtx } = tradeinCtx
          saveCtx({ ...ctx, tradein: { ...ctx.tradein, ticket_url: url } })
        }
      } else {
        setSavedLocal(preview)
        setFile(null)
        setPreview('')
      }
      setCameraError('')
      setMode('view')
      setOpen(false)
      if (onDone !== undefined) onDone()
    } catch (error: any) {
      console.log('🚀 ~ handleSend ~ error:', error)
      enqueueSnackbar('No fue posible cargar el ticket de venta.', { variant: 'error' })
      setSending(false)
    }
  }

  return (
    <>
      <Button
        color='secondary'
        variant={hasTicket ? 'flat' : 'shadow'}
        size='sm'
        className='ml-2'
        endContent={hasTicket ? <MdVisibility style={{ fontSize: '15px' }} /> : <MdReceiptLong style={{ fontSize: '15px' }} />}
        onPress={handleOpen}
      >
        {hasTicket ? 'Ver Ticket de Venta' : 'Cargar Ticket de Venta'}
      </Button>
      <Modal backdrop='blur' size='lg' scrollBehavior='inside' isOpen={open} onClose={handleClose}>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>Ticket de Venta</ModalHeader>
          <ModalBody>
            <input
              type='file'
              accept='image/png, image/jpg, image/jpeg'
              capture='environment'
              className='hidden'
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            {mode === 'view'
              ? (
                <div className='flex flex-col items-center gap-2'>
                  {/* Ticket ya cargado: solo se muestra, no se reemplaza. */}
                  <img src={viewSrc} alt='Ticket de venta' className='max-w-full md:max-w-md rounded-md' />
                </div>
                )
              : (
                <div
                  style={{
                    border: file !== null ? '1px solid #ccc' : '1px dashed #ff8181',
                    borderRadius: '5px',
                    padding: '12px',
                    textAlign: 'center',
                    backgroundColor: file !== null ? '#fff' : '#ffefef',
                    color: '#000000'
                  }}
                >
                  {file === null
                    ? (
                      <div className='flex flex-col items-center gap-2'>
                        {/* El video queda montado mientras no hay foto para poder engancharle el stream. */}
                        <video
                          ref={videoRef}
                          playsInline
                          muted
                          autoPlay
                          className={cameraReady ? 'w-full max-h-[320px] rounded-md bg-black' : 'hidden'}
                        />
                        {starting && (
                          <div className='flex items-center gap-2'>
                            <Spinner size='sm' />
                            <span className='text-sm'>Abriendo la cámara...</span>
                          </div>
                        )}
                        {!starting && cameraError !== '' && <p className='text-sm'>{cameraError}</p>}
                        <div className='flex flex-wrap items-center justify-center gap-2'>
                          {cameraReady && (
                            <Button size='sm' color='warning' startContent={<MdPhotoCamera />} onPress={takePhoto}>
                              Tomar fotografía
                            </Button>
                          )}
                          {!starting && !cameraReady && (
                            <Button size='sm' color='warning' startContent={<MdPhotoCamera />} onPress={startCamera}>
                              Abrir cámara
                            </Button>
                          )}
                          <Button size='sm' variant='flat' startContent={<MdUploadFile />} onPress={openFilePicker}>
                            Adjuntar desde el dispositivo
                          </Button>
                        </div>
                        <p className='text-xs'>(Solo se permiten archivos .png, .jpg y .jpeg)</p>
                      </div>
                      )
                    : (
                      <div className='flex flex-col items-center gap-2'>
                        <img src={preview} alt='Ticket de venta' className='max-w-full md:max-w-md rounded-md' />
                        <Button size='sm' variant='flat' startContent={<MdRefresh />} onPress={retake} isDisabled={sending}>
                          Repetir fotografía
                        </Button>
                      </div>
                      )}
                </div>
                )}
          </ModalBody>
          <ModalFooter>
            <Button color='danger' onPress={handleClose} isDisabled={sending}>
              Cerrar
            </Button>
            {mode === 'capture' && (
              <Button color='primary' onPress={handleSend} isDisabled={file === null} isLoading={sending}>
                {sending ? 'Enviando...' : 'Enviar'}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default UploadTicket
